import { supabase } from '../supabase/client';

// Recipe API functions
export const recipeAPI = {
  // Get all recipes
  async getAll() {
    const { data, error } = await supabase
      .from('recipes')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  // Get recipe by ID
  async getById(id) {
    const { data, error } = await supabase
      .from('recipes')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data;
  },

  // Create new recipe
  async create(recipe) {
    // Get current user if authenticated
    const { data: { user } } = await supabase.auth.getUser();
    
    // Add user_id if user is authenticated, otherwise leave as null for anonymous
    const recipeWithUser = {
      ...recipe,
      user_id: user?.id || null
    };
    
    const { data, error } = await supabase
      .from('recipes')
      .insert([recipeWithUser])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Update recipe
  async update(id, updates) {
    const { data, error } = await supabase
      .from('recipes')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Delete recipe
  async delete(id) {
    const { error } = await supabase
      .from('recipes')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Search recipes
  async search(query, tags = []) {
    let queryBuilder = supabase
      .from('recipes')
      .select('*');

    if (query) {
      queryBuilder = queryBuilder.or(`title.ilike.%${query}%,description.ilike.%${query}%`);
    }

    if (tags.length > 0) {
      queryBuilder = queryBuilder.overlaps('tags', tags);
    }

    const { data, error } = await queryBuilder.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  }
};

// Drink API functions
export const drinkAPI = {
  // Get all drinks
  async getAll(alcoholicOnly = null) {
    let queryBuilder = supabase
      .from('drinks')
      .select('*');

    if (alcoholicOnly !== null) {
      queryBuilder = queryBuilder.eq('is_alcoholic', alcoholicOnly);
    }

    const { data, error } = await queryBuilder.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  // Get drink by ID
  async getById(id) {
    const { data, error } = await supabase
      .from('drinks')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data;
  },

  // Create new drink
  async create(drink) {
    // Get current user if authenticated
    const { data: { user } } = await supabase.auth.getUser();
    
    // Add user_id if user is authenticated, otherwise leave as null for anonymous
    const drinkWithUser = {
      ...drink,
      user_id: user?.id || null
    };
    
    const { data, error } = await supabase
      .from('drinks')
      .insert([drinkWithUser])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Update drink
  async update(id, updates) {
    const { data, error } = await supabase
      .from('drinks')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Delete drink
  async delete(id) {
    const { error } = await supabase
      .from('drinks')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Search drinks
  async search(query, tags = [], alcoholicOnly = null) {
    let queryBuilder = supabase
      .from('drinks')
      .select('*');

    if (query) {
      queryBuilder = queryBuilder.or(`title.ilike.%${query}%,description.ilike.%${query}%`);
    }

    if (tags.length > 0) {
      queryBuilder = queryBuilder.overlaps('tags', tags);
    }

    if (alcoholicOnly !== null) {
      queryBuilder = queryBuilder.eq('is_alcoholic', alcoholicOnly);
    }

    const { data, error } = await queryBuilder.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  }
};

// Recipe Schedule API functions
export const recipeScheduleAPI = {
  // Get scheduled recipes for a date range
  async getScheduledRecipes(startDate, endDate) {
    const { data, error } = await supabase
      .from('recipe_schedules')
      .select(`
        *,
        recipes (
          id,
          title,
          description,
          ingredients,
          instructions,
          image_url,
          prep_time,
          cook_time,
          servings,
          difficulty,
          tags,
          source_url,
          user_id
        )
      `)
      .gte('scheduled_date', startDate)
      .lte('scheduled_date', endDate)
      .order('scheduled_date', { ascending: true });
    
    if (error) throw error;
    return data;
  },

  // Schedule a recipe for a specific date
  async scheduleRecipe(recipeId, scheduledDate, mealType = 'dinner') {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated to schedule recipes');

    const scheduleData = {
      recipe_id: recipeId,
      user_id: user.id,
      scheduled_date: scheduledDate,
      meal_type: mealType
    };

    const { data, error } = await supabase
      .from('recipe_schedules')
      .insert([scheduleData])
      .select(`
        *,
        recipes (
          id,
          title,
          description,
          ingredients,
          instructions,
          image_url,
          prep_time,
          cook_time,
          servings,
          difficulty,
          tags,
          source_url,
          user_id
        )
      `)
      .single();
    
    if (error) throw error;
    return data;
  },

  // Remove a recipe from schedule
  async unscheduleRecipe(scheduleId) {
    const { error } = await supabase
      .from('recipe_schedules')
      .delete()
      .eq('id', scheduleId);
    
    if (error) throw error;
  },

  // Get all schedules for a specific recipe
  async getRecipeSchedules(recipeId) {
    const { data, error } = await supabase
      .from('recipe_schedules')
      .select('*')
      .eq('recipe_id', recipeId)
      .order('scheduled_date', { ascending: true });
    
    if (error) throw error;
    return data;
  },

  // Get scheduled recipes for a specific date
  async getScheduledRecipesForDate(date) {
    const { data, error } = await supabase
      .from('recipe_schedules')
      .select(`
        *,
        recipes (
          id,
          title,
          description,
          ingredients,
          instructions,
          image_url,
          prep_time,
          cook_time,
          servings,
          difficulty,
          tags,
          source_url,
          user_id
        )
      `)
      .eq('scheduled_date', date)
      .order('meal_type', { ascending: true });
    
    if (error) throw error;
    return data;
  }
};

// Authentication functions
export const authAPI = {
  // Sign in with magic link
  async signInWithMagicLink(email) {
    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}`,
      }
    });
    
    if (error) throw error;
    return data;
  },

  // Sign in with password (kept for backward compatibility)
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) throw error;
    return data;
  },

  // Sign out
  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  // Get current user
  async getCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  },

  // Request password reset (kept for backward compatibility)
  async requestPasswordReset(email) {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}`,
    });
    
    if (error) throw error;
    return data;
  },

  // Update password (kept for backward compatibility)
  async updatePassword(newPassword) {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword
    });
    
    if (error) throw error;
    return data;
  },

  // Listen to auth changes
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback);
  }
};